import React, { useState, useEffect } from 'react';
import { Sparkles, MessageCircle } from 'lucide-react';

interface LabPortalProps {
  onOpen: () => void;
  contextualMessage?: string;
  recipeTitle?: string;
  isProcessing?: boolean;
}

export const LabPortal: React.FC<LabPortalProps> = ({
  onOpen,
  contextualMessage,
  recipeTitle,
  isProcessing
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showWhisper, setShowWhisper] = useState(false);

  useEffect(() => {
    if (contextualMessage && !isHovered) {
      const timer = setTimeout(() => setShowWhisper(true), 3000);
      return () => clearTimeout(timer);
    }
  }, [contextualMessage, isHovered]);

  useEffect(() => {
    if (isHovered) {
      setShowWhisper(false);
    }
  }, [isHovered]);

  const displayMessage = contextualMessage || (recipeTitle ? `Working on ${recipeTitle}?` : 'Need a hand, Chef?');

  return (
    <div className="fixed bottom-20 md:bottom-6 right-4 md:right-6 z-[1000]">
      {/* Contextual Whisper */}
      {showWhisper && !isHovered && (
        <div className="absolute bottom-full right-0 mb-3 animate-fadeInUp">
          <div className="bg-white dark:bg-stone-800 rounded-xl shadow-lg border border-stone-200 dark:border-stone-700 px-4 py-2.5 max-w-[200px]">
            <p className="text-xs text-stone-700 dark:text-stone-300 font-medium">{displayMessage}</p>
            <div className="absolute -bottom-1.5 right-6 w-3 h-3 bg-white dark:bg-stone-800 border-r border-b border-stone-200 dark:border-stone-700 rotate-45" />
          </div>
        </div>
      )}

      {/* Portal Button */}
      <button
        onClick={onOpen}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        aria-label="Open Lab Assistant"
        className="relative group"
      >
        {/* Subtle glow ring */}
        <div className="absolute inset-0 rounded-full bg-terracotta-500/20 blur-md animate-pulse-slow" />

        {/* Main button */}
        <div className={`
          relative flex items-center gap-2.5 p-1.5 pr-4
          bg-terracotta-500 hover:bg-terracotta-600
          text-white rounded-full 
          shadow-lg hover:shadow-xl
          transition-all duration-300 ease-out
          ${isHovered ? 'scale-105' : 'scale-100'}
          ${isProcessing ? 'animate-breathe' : ''}
        `}>
          {/* Avatar container with steam effect when processing */}
          <div className="relative">
            {/* Steam particles when processing */}
            {isProcessing && (
              <>
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-1 h-3 bg-white/30 rounded-full animate-steam-1" />
                <div className="absolute -top-3 left-1/3 w-1 h-2 bg-white/20 rounded-full animate-steam-2" />
                <div className="absolute -top-2 right-1/3 w-1 h-2.5 bg-white/25 rounded-full animate-steam-3" />
              </>
            )}
            
            {/* Avatar */}
            <div className="w-12 h-12 rounded-full border-2 border-white/40 bg-white/10 flex items-center justify-center overflow-hidden">
              <img
                src="/pers4.jpg"
                alt="Lab Assistant"
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.nextElementSibling?.classList.remove('hidden');
                }}
              />
              <Sparkles className="w-6 h-6 text-white hidden" />
            </div>

            {/* Online indicator */}
            <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-sage-400 rounded-full border-2 border-white dark:border-[#1A1714]" />
          </div>

          {/* Label */}
          <span className="font-medium text-sm hidden md:inline whitespace-nowrap">
            {isProcessing ? 'Working...' : 'Lab Assistant'}
          </span>

          {/* MessageCircle icon for mobile */}
          <MessageCircle className="w-5 h-5 md:hidden" />
        </div>
      </button>
    </div>
  );
};
